import { httpsCallable, type Functions } from 'firebase/functions'
import type { Firestore } from 'firebase/firestore'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { fetchUserProfiles } from '../firestore/UserProfile'
import { getSectionPassDocRef } from '../firestore/UserProfileSectionPass'
import { getDocWithError } from '../firestore-mobx/fetch'

export type OptOutStudentRow = {
  email: string
  firstName: string
  lastName: string
  hasPurchased: boolean
}

export async function fetchOptOutStudentData(
  repository: FirebaseRepository,
  opts: {
    sectionId: string
    optOutUserIds: string[]
    functions: Functions
    firestore: Firestore
  }
): Promise<OptOutStudentRow[]> {
  const { sectionId, optOutUserIds, functions: fnRef, firestore } = opts

  const getOptOutEmails = httpsCallable<
    { sectionId: string },
    {
      success: boolean
      optOutEmails?: string[]
      unresolvedEmails?: string[]
    }
  >(fnRef, 'getOptOutEmails')

  const [optOutEmailsResult, userProfiles, sectionPassResults] =
    await Promise.all([
      getOptOutEmails({ sectionId }),
      optOutUserIds.length > 0
        ? fetchUserProfiles(repository, { userIds: optOutUserIds })
        : Promise.resolve([]),
      Promise.all(
        optOutUserIds.map(async (userId) => {
          const docRef = getSectionPassDocRef(firestore, {
            userId,
            sectionId,
          })
          const snap = await getDocWithError(docRef, 'FetchOptOutSectionPass')
          return [userId, snap.exists()] as const
        })
      ),
    ])

  const unresolvedEmails: string[] =
    optOutEmailsResult.data.unresolvedEmails ?? []

  const userProfileMap = new Map(userProfiles.map((u) => [u.id, u]))
  const hasPurchasedMap = new Map(sectionPassResults)
  const publicUsers = repository.userStore.getUsers(optOutUserIds)

  const resolvedRows: OptOutStudentRow[] = optOutUserIds.map((userId) => {
    const profile = userProfileMap.get(userId)
    const publicUser = publicUsers.find((u) => u.id === userId)
    return {
      email: profile?.data.emailAddress ?? '',
      firstName: publicUser?.data.firstName ?? '',
      lastName: publicUser?.data.lastName ?? '',
      hasPurchased: hasPurchasedMap.get(userId) ?? false,
    }
  })

  const resolvedEmailSet = new Set(
    resolvedRows.map((r) => r.email.toLowerCase())
  )
  const unresolvedRows: OptOutStudentRow[] = unresolvedEmails
    .filter((email) => !resolvedEmailSet.has(email.toLowerCase()))
    .map((email) => ({
      email,
      firstName: '',
      lastName: '',
      hasPurchased: false,
    }))

  return [...resolvedRows, ...unresolvedRows]
}
